<template>
  <div class="customer-boolean">
    <button
      type="button"
      class="customer-switch"
      role="switch"
      :aria-label="label"
      :aria-checked="value"
      :aria-busy="saving"
      :disabled="saving"
      @click="save"
    >
      <span class="customer-switch-thumb" />
    </button>
    <span role="status" class="customer-switch-status">{{ saving ? 'Saving…' : saved ? 'Saved' : '' }}</span>
    <p v-if="error" role="alert" class="text-danger">Could not save. Setting restored. Try again.</p>
  </div>
</template>
<script lang="ts">
import type { CustomerBooleanSettingProps } from '@/types/Components/Customer'
import type { RequestCompany } from '@/types/Models/Company'
import { ref, watch, defineComponent, type Ref, type PropType } from 'vue'
import { useCustomersStore } from '@/store/customers'
import CompanyProxy from '@/proxies/CompanyProxy'

export default defineComponent({
  name: 'CustomerBooleanSetting',
  props: {
    company: { type: Object as PropType<CustomerBooleanSettingProps['company']>, required: true },
    field: { type: String as PropType<CustomerBooleanSettingProps['field']>, required: true },
    label: { type: String, required: true },
  },
  setup(props) {
    const customers = useCustomersStore()
    const value: Ref<boolean> = ref(!!props.company[props.field])
    const saving: Ref<boolean> = ref(false)
    const error: Ref<boolean> = ref(false)
    const saved: Ref<boolean> = ref(false)
    watch(
      () => props.company[props.field],
      (newValue) => {
        value.value = !!newValue
      },
    )
    function save(): void {
      if (saving.value) return
      const previousValue = value.value
      value.value = !previousValue
      saved.value = false
      saving.value = true
      error.value = false
      const data: Partial<RequestCompany> = { [props.field]: value.value }
      if (props.field === 'isBufferingEnabled') {
        data.bufferingOptions = value.value
          ? props.company.bufferingOptions || { cutoffTime: null, itemsCount: null, minutesDiff: 15 }
          : null
      }
      new CompanyProxy()
        .update(props.company.id, data)
        .then(() => {
          const customer = customers.listCustomers?.find((item) => item.id === props.company.id)
          if (customer) {
            customer[props.field] = value.value
            if (props.field === 'isBufferingEnabled') customer.bufferingOptions = data.bufferingOptions ?? null
          }
          saved.value = true
        })
        .catch(() => {
          value.value = previousValue
          error.value = true
        })
        .finally(() => {
          saving.value = false
        })
    }
    return { customers, value, saving, error, saved, save }
  },
})
</script>
